/**
 * GARAGE panel (v49) — the chassis you have claimed, and which one deploys.
 *
 * Speaks the SOUL MERIDIAN language (the socialPanel precedent): near-black glass,
 * the fraying line as a header rule stirred on every change, thin tracked uppercase
 * labels, controls that are text. This is the migration path off the older `.inv-*`
 * blue-glass vocabulary; the bag grid itself stays put for now.
 *
 * LOCKED chassis are listed, not hidden. A collection you cannot see the shape of
 * is not a collection — the greyed rows are the reason to go looking for keycards.
 */
import { VEHICLES, type VehicleDef } from "@hyperlight/shared";
import type { GarageModel } from "../rpg/garage";
import { mountMeridianRule } from "./meridian";
import type { MeridianLine } from "./meridianLine";

/** One line of plain-language character per chassis — the garage is a showroom,
 *  and a table of identical stat blocks tells a player nothing about the choice. */
const BLURBS: Record<string, string> = {
  "scout-speeder": "agile · forgiving · standard issue",
  "interceptor-speeder": "faster · twitchier · less forgiving",
  "hauler-speeder": "slow · stable · four seats",
  "hover-bike": "straight lines, very fast ones",
  "m66-stingray": "powered flight · prone pilot",
  banshee: "single seat · climbs like nothing else",
};

/** Catalog order, so the list never reshuffles under the player's cursor. */
function catalog(): VehicleDef[] {
  return Object.values(VEHICLES);
}

export class GaragePanel {
  visible = false;
  private el: HTMLElement;
  private bodyEl: HTMLElement;
  private rule: MeridianLine;
  /** The rev the body was last drawn from — re-render only on real change. */
  private drawnRev = -1;

  constructor(
    private garage: GarageModel,
    private handlers: {
      /** Make `id` the active chassis (null stows it). */
      onActivate: (id: string | null) => void;
      /** Call `id` down beside the player (also makes it active). */
      onSummon: (id: string) => void;
      /** Send the parked chassis away. */
      onDismiss: () => void;
      /** The chassis parked in the world right now, or null. */
      deployed: () => string | null;
      onClose: () => void;
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "garage-panel";
    this.el.className = "mrd-panel";
    this.el.style.zIndex = "42";
    this.el.innerHTML = `
      <div class="mrd-head">
        <span class="mrd-title">garage</span>
        <span class="mrd-hint">esc</span>
      </div>
      <div class="mrd-rule"></div>
      <div class="gp-body mrd-body"></div>`;
    document.body.appendChild(this.el);
    this.bodyEl = this.el.querySelector(".gp-body")!;
    this.rule = mountMeridianRule(this.el.querySelector(".mrd-rule")!, 0.36);

    this.bodyEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-act]") as HTMLElement | null;
      if (!btn || btn.hasAttribute("disabled")) return;
      const act = btn.dataset.act;
      const id = btn.dataset.veh ?? null;
      if (act === "activate" && id) this.handlers.onActivate(id);
      else if (act === "summon" && id) this.handlers.onSummon(id);
      else if (act === "dismiss") this.handlers.onDismiss();
      this.rule.stir(0.8); // "whenever the screen changes, the line moves"
      this.render();
    });
  }

  open(): void {
    this.visible = true;
    this.el.style.display = "flex";
    this.rule.start();
    this.rule.stir(1);
    this.drawnRev = -1;
    this.render();
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.style.display = "none";
    this.rule.stop(); // a drifting line behind a closed panel is wasted frames
    this.handlers.onClose();
  }

  toggle(): void {
    if (this.visible) this.close();
    else this.open();
  }

  /** Re-render if the model moved (called from the frame loop while open). */
  sync(): void {
    if (this.visible && this.drawnRev !== this.garage.rev) this.render();
  }

  render(): void {
    this.drawnRev = this.garage.rev;
    const owned = this.garage.vehicles;
    const out = this.handlers.deployed();
    const rows = catalog().map((def) => {
      const has = owned.includes(def.id);
      const active = this.garage.active === def.id;
      const parked = out === def.id;
      const blurb = BLURBS[def.id] ?? (def.flight ? "powered flight" : "ground chassis");
      // Short status words: the column is fixed-width so every row's status reads
      // at the same x, and the footer note already explains where keycards come in.
      const meta = parked
        ? `<span class="mrd-meta">deployed</span>`
        : active
          ? `<span class="mrd-meta off">active</span>`
          : has
            ? `<span class="mrd-meta off">in garage</span>`
            : `<span class="mrd-meta off">locked</span>`;
      // Summon is the verb the panel exists for; "set active" only picks what V calls.
      const act = parked
        ? `<button class="mrd-act" data-act="dismiss">stow</button>`
        : has
          ? `<button class="mrd-act" data-act="summon" data-veh="${def.id}">summon</button>`
          : "";
      return `
        <div class="mrd-row${has ? "" : " dim"}">
          <span class="mrd-who">${def.name}<i class="gp-blurb">${blurb}</i></span>
          ${meta}
          <span class="mrd-acts end">${act}</span>
        </div>`;
    }).join("");

    const count = `${owned.length} / ${catalog().length}`;
    this.bodyEl.innerHTML = `
      <div class="mrd-heading">chassis <span class="gp-count">${count}</span></div>
      ${rows}
      <p class="gp-note">${this.garage.seeded
        ? "Summoning calls a chassis down beside you — one at a time. V summons the active one. Using a keycard adds a chassis here permanently."
        : "reading your garage…"}</p>`;
  }
}
